import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const inputCls = "w-full rounded-xl border border-forest/15 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-leaf/50 focus:border-leaf";

export default function AdminContent() {
  const [services, setServices] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [newArea, setNewArea] = useState({ name: "", blurb: "" });

  const load = () => {
    Promise.all([api.get("/services"), api.get("/areas")])
      .then(([s, a]) => { setServices(s.data); setAreas(a.data); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const saveService = async (svc) => {
    setSavingId(svc.id);
    try {
      await api.put(`/admin/services/${svc.id}`, {
        title: svc.title, summary: svc.summary,
        items: svc.items.filter(Boolean),
        image: svc.image, image_alt: svc.image_alt,
      });
      toast.success(`${svc.title} saved`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingId(null);
    }
  };

  const updateSvc = (id, k, v) => setServices((ss) => ss.map((s) => (s.id === id ? { ...s, [k]: v } : s)));
  const updateArea = (id, k, v) => setAreas((as) => as.map((a) => (a.id === id ? { ...a, [k]: v } : a)));

  const saveArea = async (area) => {
    setSavingId(area.id);
    try {
      await api.put(`/admin/areas/${area.id}`, { name: area.name, blurb: area.blurb });
      toast.success("Area saved");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSavingId(null); }
  };

  const addArea = async () => {
    if (newArea.name.trim().length < 2) return toast.error("Enter an area name");
    try {
      await api.post("/admin/areas", newArea);
      setNewArea({ name: "", blurb: "" });
      load();
      toast.success("Area added");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeArea = async (id) => {
    if (!window.confirm("Remove this service area?")) return;
    try {
      await api.delete(`/admin/areas/${id}`);
      setAreas((as) => as.filter((a) => a.id !== id));
      toast.success("Area removed");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-leaf" />;

  return (
    <div className="space-y-10" data-testid="admin-content-panel">
      <section>
        <h2 className="font-display text-xl font-bold text-forest mb-5">Services</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {services.map((svc) => (
            <div key={svc.id} className="rounded-2xl bg-white border border-forest/10 p-6" data-testid={`service-editor-${svc.category}`}>
              <p className="text-xs font-semibold uppercase tracking-wider text-leaf-hover mb-3">{svc.category}</p>
              <div className="space-y-3">
                <input className={inputCls} value={svc.title} onChange={(e) => updateSvc(svc.id, "title", e.target.value)} data-testid={`service-title-${svc.category}`} />
                <textarea rows={3} className={inputCls} value={svc.summary} onChange={(e) => updateSvc(svc.id, "summary", e.target.value)} data-testid={`service-summary-${svc.category}`} />
                <div>
                  <label className="block text-xs font-semibold text-forest/70 mb-1">Included items (one per line)</label>
                  <textarea rows={8} className={inputCls} value={svc.items.join("\n")} onChange={(e) => updateSvc(svc.id, "items", e.target.value.split("\n"))} data-testid={`service-items-${svc.category}`} />
                </div>
                <input className={inputCls} value={svc.image} onChange={(e) => updateSvc(svc.id, "image", e.target.value)} placeholder="Image URL" data-testid={`service-image-${svc.category}`} />
                <input className={inputCls} value={svc.image_alt} onChange={(e) => updateSvc(svc.id, "image_alt", e.target.value)} placeholder="Image alt text" data-testid={`service-image-alt-${svc.category}`} />
                <button onClick={() => saveService(svc)} disabled={savingId === svc.id} data-testid={`service-save-${svc.category}`} className="inline-flex items-center gap-2 rounded-full bg-forest px-6 py-2.5 text-sm font-bold text-white hover:bg-forest-soft disabled:opacity-60">
                  {savingId === svc.id && <Loader2 className="w-4 h-4 animate-spin" />} Save
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-display text-xl font-bold text-forest mb-5">Service Areas</h2>
        <div className="space-y-3">
          {areas.map((area) => (
            <div key={area.id} className="rounded-2xl bg-white border border-forest/10 p-5 flex flex-wrap items-start gap-3" data-testid={`area-editor-${area.id}`}>
              <input className={`${inputCls} sm:max-w-[220px]`} value={area.name} onChange={(e) => updateArea(area.id, "name", e.target.value)} data-testid={`area-name-${area.id}`} />
              <input className={`${inputCls} flex-1 min-w-[200px]`} value={area.blurb} onChange={(e) => updateArea(area.id, "blurb", e.target.value)} data-testid={`area-blurb-${area.id}`} />
              <button onClick={() => saveArea(area)} disabled={savingId === area.id} className="rounded-full bg-forest px-5 py-2.5 text-xs font-bold text-white hover:bg-forest-soft" data-testid={`area-save-${area.id}`}>Save</button>
              <button onClick={() => removeArea(area.id)} className="p-2.5 text-slate-400 hover:text-red-600" aria-label="Remove area" data-testid={`area-delete-${area.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
        <div className="mt-4 rounded-2xl bg-mint/40 border border-leaf/20 p-5 flex flex-wrap items-center gap-3">
          <input className={`${inputCls} sm:max-w-[220px]`} placeholder="New area name" value={newArea.name} onChange={(e) => setNewArea({ ...newArea, name: e.target.value })} data-testid="area-new-name" />
          <input className={`${inputCls} flex-1 min-w-[200px]`} placeholder="Short description" value={newArea.blurb} onChange={(e) => setNewArea({ ...newArea, blurb: e.target.value })} data-testid="area-new-blurb" />
          <button onClick={addArea} className="inline-flex items-center gap-2 rounded-full bg-leaf px-5 py-2.5 text-xs font-bold text-forest-deep hover:bg-mint" data-testid="area-add-button">
            <Plus className="w-4 h-4" /> Add Area
          </button>
        </div>
        <p className="mt-3 text-xs text-slate-500">Only list areas Glow Green genuinely serves - this list appears on the public Service Areas page.</p>
      </section>
    </div>
  );
}
